import { Input } from '../common/Input';
import { useSreStats } from '../../hooks/useBotAdmin';
import type { SreBreakdown, SreConfigMode } from '../../services/api/botAdmin';

/** Phase 127 (D-24, D-25): the DB CHECK and the PUT schema both require budgets >= 1. */
export const SRE_BUDGET_MIN = 1;

/** The form-owned editable state of the block; budgets stay strings while typing. */
export interface SreSettingsDraft {
  enabled: boolean;
  mode: SreConfigMode;
  tickBudget: string;
  dailyBudget: string;
  auditBudget: string;
}

export const SRE_SETTINGS_DEFAULTS: SreSettingsDraft = {
  enabled: false,
  mode: 'triage',
  tickBudget: '3',
  dailyBudget: '10',
  auditBudget: '5',
};

export function isValidSreBudget(value: string): boolean {
  const n = Number(value);
  return value.trim() !== '' && Number.isInteger(n) && n >= SRE_BUDGET_MIN;
}

/** True when all three budgets would pass the server's `minimum: 1` integer rule. */
export function isValidSreSettings(draft: SreSettingsDraft): boolean {
  return (
    isValidSreBudget(draft.tickBudget) &&
    isValidSreBudget(draft.dailyBudget) &&
    isValidSreBudget(draft.auditBudget)
  );
}

/**
 * Phase 127 (Plan 127-07 / R9, R10; D-23, D-26) — the SRE agent block of the Bot Admin
 * project queue form: how accurate the agent has been, and why the Triage to Backlog switch is
 * still closed.
 */

/** An em dash when nothing is decided; a whole percentage otherwise (rounded server-side). */
function formatAccuracy(accuracyPercent: number | null): string {
  return accuracyPercent === null ? '—' : `${accuracyPercent}%`;
}

function breakdownLine(label: string, testId: string, b: SreBreakdown) {
  return (
    <li data-testid={testId}>
      {label}: {b.filed} filed, {b.correct} correct, {b.wrong} wrong, {b.pending} pending
    </li>
  );
}

function Stat({ label, value, testId }: { label: string; value: string | number; testId: string }) {
  return (
    <div className="min-w-[5rem]">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-base font-semibold text-gray-900" data-testid={testId}>
        {value}
      </p>
    </div>
  );
}

interface SreAgentSectionProps {
  projectId: number;
  /** The mode stored on the server; Backlog stays selectable only when it is already stored. */
  storedMode: SreConfigMode;
  draft: SreSettingsDraft;
  onChange: (patch: Partial<SreSettingsDraft>) => void;
}

export function SreAgentSection({ projectId, storedMode, draft, onChange }: SreAgentSectionProps) {
  const { data: stats, isLoading, isError } = useSreStats(projectId);
  // D-23: the server enforces the gate; closing the option here is cosmetic. It stays closed
  // until the stats say otherwise (loading and error included), unless Backlog is already stored.
  const backlogClosed = storedMode !== 'backlog' && !(stats?.backlogSwitch.allowed ?? false);
  // The semi-automatic mode is not gated by the record: the server accepts it any time.

  return (
    <div
      className="space-y-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-3"
      data-testid="queue-config-sre-group"
    >
      <p className="text-sm font-semibold text-gray-900">SRE agent</p>

      <div className="flex flex-wrap items-start gap-3">
        <label className="flex items-center gap-1.5 pt-6 text-sm text-gray-700">
          <input
            id="queue-config-sre-enabled"
            type="checkbox"
            checked={draft.enabled}
            onChange={(e) => onChange({ enabled: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          SRE agent enabled
        </label>
        <div className="w-72">
          <label
            htmlFor="queue-config-sre-mode"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            SRE ticket column
          </label>
          <select
            id="queue-config-sre-mode"
            value={draft.mode}
            onChange={(e) => onChange({ mode: e.target.value as SreConfigMode })}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="triage">Triage</option>
            <option value="backlog" disabled={backlogClosed}>
              Backlog
            </option>
            <option value="auto">Auto: confident to Backlog, low and audit to Triage</option>
          </select>
          {draft.mode === 'auto' && (
            <p className="mt-1 text-xs text-gray-500" data-testid="queue-config-sre-auto-hint">
              The Robot picks confident findings from Backlog; low-confidence and audit findings wait
              in Triage for a person.
            </p>
          )}
        </div>
        <div className="w-44">
          <Input
            id="queue-config-sre-tick-budget"
            label="Max tickets per tick"
            type="number"
            min={SRE_BUDGET_MIN}
            value={draft.tickBudget}
            onChange={(e) => onChange({ tickBudget: e.target.value })}
          />
        </div>
        <div className="w-44">
          <Input
            id="queue-config-sre-daily-budget"
            label="Max tickets per day"
            type="number"
            min={SRE_BUDGET_MIN}
            value={draft.dailyBudget}
            onChange={(e) => onChange({ dailyBudget: e.target.value })}
          />
        </div>
        <div className="w-44">
          <Input
            id="queue-config-sre-audit-budget"
            label="Max tickets per audit"
            type="number"
            min={SRE_BUDGET_MIN}
            value={draft.auditBudget}
            onChange={(e) => onChange({ auditBudget: e.target.value })}
          />
        </div>
      </div>

      <div data-testid="queue-config-sre-stats">
        {isLoading ? (
          <p className="text-sm text-gray-400">Loading SRE stats…</p>
        ) : isError || !stats ? (
          <p className="text-sm text-red-600">Failed to load SRE stats.</p>
        ) : (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-4">
              <Stat label="Filed" value={stats.filed} testId="sre-stat-filed" />
              <Stat label="Correct" value={stats.correct} testId="sre-stat-correct" />
              <Stat label="Wrong" value={stats.wrong} testId="sre-stat-wrong" />
              <Stat label="Pending" value={stats.pending} testId="sre-stat-pending" />
              <Stat
                label="Accuracy"
                value={formatAccuracy(stats.accuracyPercent)}
                testId="sre-stat-accuracy"
              />
            </div>
            <ul className="space-y-0.5 text-xs text-gray-600">
              {breakdownLine('Low confidence', 'sre-breakdown-low', stats.lowConfidence)}
              {breakdownLine('Weekly audit', 'sre-breakdown-audit', stats.audit)}
            </ul>

            <div data-testid="sre-backlog-switch">
              {stats.backlogSwitch.allowed ? (
                <p className="text-sm text-green-700">
                  The record qualifies: the agent can move from Triage to Backlog.
                </p>
              ) : (
                <>
                  <p className="text-sm font-medium text-gray-700">
                    Backlog is closed until the record earns it:
                  </p>
                  <ul className="list-disc pl-5 text-sm text-gray-600">
                    {stats.backlogSwitch.reasons.map((reason) => (
                      <li key={reason.code} data-testid={`sre-reason-${reason.code}`}>
                        {reason.message}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
